import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import ContentCard from '../components/ContentCard';
import PageNav from '../components/PageNav';
import SearchBar from '../components/SearchBar';
import { formatCounts } from '../utils/labels';
import './SearchResults.css';

// Results of searching names of compartments and items, everywhere or within one place.
function SearchResults() {
  const [params] = useSearchParams();
  const q = params.get('q') || '';
  const within = params.get('within') || '';
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    setResult(null);
    setError('');
    if (!q) return undefined;
    const run = async () => {
      try {
        const response = await fetch(`/api/search?q=${encodeURIComponent(q)}${within ? `&within=${within}` : ''}`);
        const data = await response.json();
        if (cancelled) return;
        if (!response.ok) setError(data.error || `Search failed (${response.status})`);
        else setResult(data);
      } catch (e) {
        if (!cancelled) setError('Could not reach the server. If you were signed out, reload the page and sign in again.');
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [q, within]);

  const place = result && result.within;
  const total = result ? result.locations.length + result.items.length : 0;

  return (
    <div className="search-results">
      <PageNav
        trail={place ? place.trail : []}
        current="Search"
        fallback={within ? `/locations/${within}` : '/locations'}
      />

      <SearchBar
        key={`${q}|${within}`}
        within={within}
        initial={q}
        placeholder={place ? `Search within ${place.name}` : 'Search all names'}
      />

      {error && <p className="search-error">{error}</p>}
      {!q && !error && <p className="search-note">Type a name to search for.</p>}
      {q && !result && !error && <p className="search-note">Searching...</p>}

      {result && (
        <>
          <h2 className="search-summary">
            {total === 0
              ? `Nothing found for "${q}"`
              : `${total} match${total === 1 ? '' : 'es'} for "${q}"`}
            {place ? ` in ${place.name}` : ''}
          </h2>
          {result.truncated && <p className="search-note">Showing the first {result.limit} of each kind. Add more letters to narrow it down.</p>}

          {result.locations.length > 0 && (
            <section>
              <h3 className="search-heading">Compartments and places ({result.locations.length})</h3>
              <div className="card-list">
                {result.locations.map((loc) => (
                  <ContentCard
                    key={loc.id}
                    to={`/locations/${loc.id}`}
                    placeholder={loc.parentLocationId ? '🗄️' : '🏠'}
                    title={loc.name}
                    subtitle={loc.path ? `In ${loc.path}` : 'Top level'}
                    meta={formatCounts(loc)}
                  />
                ))}
              </div>
            </section>
          )}

          {result.items.length > 0 && (
            <section>
              <h3 className="search-heading">Items ({result.items.length})</h3>
              <div className="card-list">
                {result.items.map((item) => (
                  <ContentCard
                    key={item.id}
                    to={`/items/${item.id}`}
                    placeholder="📦"
                    title={item.name}
                    subtitle={item.path ? `In ${item.path}` : 'Not in a place yet'}
                    meta={item.count > 1 ? `Count: ${item.count}` : null}
                  />
                ))}
              </div>
            </section>
          )}
        </>
      )}
    </div>
  );
}

export default SearchResults;
